import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'antd';
import { Checkbox, DatePicker, Input, Popup, Select } from '_atoms';
import { SELECT_TYPES } from '_atoms/Select/constants';
import { Typography } from '_atoms/Typography';
import { getRooms } from 'store/selectors/jobSelectors';
import { BUTTON_TEXT } from 'constants/text';
import { INTERVIEW_TYPES } from 'constants/job';
import { utcTimeForRooms } from 'helpers/dateFormatter';
import { getRoomsOption } from 'actions';
import { useAppDispatch } from 'store';
import type { Moment } from 'moment';
import { fromTimeOptions, toTimeOptions } from './constants';
import './style.scss';

interface TimeOption {
  value: string;
  label: string;
  index: number;
}

interface InterviewFormValues {
  date?: Moment | null;
  from_time?: TimeOption;
  to_time?: TimeOption;
  type?: { value: string; label: string };
  locations?: Array<{ value: string; label: string }>;
  link?: string;
  create_google_event?: boolean;
}

interface InterviewUserData {
  full_name_latin?: string;
  first_name_latin?: string;
  last_name_latin?: string;
  full_name_local?: string;
  first_name_local?: string;
  last_name_local?: string;
  specialities?: Array<{ level?: string; speciality?: string }>;
}

interface PopupCreateInterviewProps {
  open: boolean;
  onClose: () => void;
  onOk: (values: InterviewFormValues) => void;
  initialValues: InterviewFormValues;
  empty?: boolean;
  userData?: InterviewUserData;
  accessToCalendar?: boolean;
}

export const PopupCreateInterview: React.FC<PopupCreateInterviewProps> = ({
  open,
  onClose,
  onOk,
  initialValues,
  empty,
  userData = {},
  ...props
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [values, setValues] = useState<InterviewFormValues>(initialValues);
  const rooms = useSelector(getRooms);
  const onSubmit = () => onOk(values);
  const onChangeValue = (key: string) => (value: unknown) => setValues(v => ({ ...v, [key]: value }));

  useEffect(() => {
    setValues(initialValues);
  }, [open, initialValues]);

  useEffect(() => {
    if (values.from_time && values.to_time && values.date && open) {
      dispatch(
        getRoomsOption(
          values.date.format('YYYY-MM-DD'),
          utcTimeForRooms(values.from_time.value),
          utcTimeForRooms(values.to_time.value)
        )
      );
    }
  }, [values.from_time, values.date, values.to_time, open]);

  return (
    <Popup
      className="popup-create-interview mobile-full-size"
      open={open}
      title={
        empty ? t('translation:popup_create_interview.title_add') : t('translation:popup_create_interview.title_edit')
      }
      cancelButtonProps={{ onClick: onClose, children: BUTTON_TEXT.CANCEL }}
      okButtonProps={{
        onClick: onSubmit,
        children: empty ? BUTTON_TEXT.ADD_INTERVIEW : BUTTON_TEXT.SAVE,
        disabled: !values.date || !values.type,
      }}
    >
      <div className="user-info">
        <Typography.Text className="user-info__title">
          {userData.full_name_latin || `${userData.first_name_latin} ${userData.last_name_latin}`}
        </Typography.Text>
        <Typography.Caption className="user-info__subtitle">
          {userData.full_name_local || `${userData.first_name_local} ${userData.last_name_local}`}
        </Typography.Caption>
        <Typography.Caption className="user-info__subtitle">
          {userData.specialities?.[0]?.level} {userData.specialities?.[0]?.speciality}
        </Typography.Caption>
      </div>
      <Row gutter={16}>
        <Col span={12}>
          <DatePicker
            label={t('translation:popup_create_interview.label_date')}
            value={values.date}
            onChange={onChangeValue('date')}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Select
            label={t('translation:popup_create_interview.label_from')}
            inPopup
            options={fromTimeOptions}
            value={values.from_time}
            onChange={(v: TimeOption) => {
              onChangeValue('to_time')(toTimeOptions[v.index + 1]);
              onChangeValue('from_time')(v);
            }}
          />
        </Col>
        <Col span={12}>
          <Select
            label={t('translation:popup_create_interview.label_to')}
            inPopup
            options={toTimeOptions.slice(values.from_time.index, toTimeOptions.length)}
            value={values.to_time}
            onChange={onChangeValue('to_time')}
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Select
            label={t('translation:popup_create_interview.label_meeting_type')}
            placeholder={t('translation:popup_create_interview.ph_meeting_type')}
            options={INTERVIEW_TYPES.filter(el => !(!props.accessToCalendar && el === INTERVIEW_TYPES[0])).map(
              item => ({ value: item, label: item })
            )}
            value={values.type}
            onChange={onChangeValue('type')}
          />
          {values.type?.value === INTERVIEW_TYPES[1] && (
            <Typography.Label className="automatically-message">
              {t('translation:popup_create_interview.text_link_generated_automatically')}
            </Typography.Label>
          )}
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Select
            label={t('translation:popup_create_interview.label_meeting_room')}
            placeholder={t('translation:popup_create_interview.ph_meeting_room')}
            options={rooms.map(item => ({ value: item.id, label: item.name }))}
            value={values.locations}
            mode={SELECT_TYPES.MULTIPLE}
            onChange={onChangeValue('locations')}
            disabled={!props.accessToCalendar}
          />
        </Col>
      </Row>
      {values.type?.value === 'Other' && (
        <Row>
          <Col span={24}>
            <Input
              label={t('translation:popup_create_interview.label_meeting_link')}
              value={values.link}
              onChange={e => onChangeValue('link')(e.target.value)}
              placeholder={t('translation:popup_create_interview.ph_meeting_link')}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col span={24}>
          <Checkbox
            label={t('translation:popup_create_interview.label_add_to_calendar')}
            isSelected={values.create_google_event}
            onChange={e => onChangeValue('create_google_event')(e.target.checked)}
            disabled={!props.accessToCalendar}
          />
        </Col>
      </Row>
    </Popup>
  );
};
